import type { Currency } from '@etiquetachef/types';

/**
 * Formatadores de exibição do BI e do Financeiro do admin: moeda em centavos, inteiros,
 * percentuais, variação e data `yyyymmdd`.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200 (rota /dashboard/finance).
 *
 * Por quê: todo valor monetário do backend é CENTAVO inteiro (`Cents` em
 * `packages/types/src/aggregations.ts`; convenção do Financeiro em `apps/admin/src/lib/money.ts`,
 * owner 2026-06-21). A divisão por 100 acontece só aqui, na saída — quem já dividiu antes
 * (os charts plotam `/ 100`) tem de reconverter antes de chamar, senão exibe 100× menor.
 *
 * Contrato: `formatUsdCents`/`formatCurrencyCents` recebem centavos; `formatPct` recebe fração
 * (0.153 → "15,3%"), não percentual; `formatDelta` devolve `'—'` para `null`, não-finito e zero
 * (`FLAT` e `NA` são distintos para o ícone do `KpiCard`). `formatYyyymmddDisplay` não valida:
 * string fora de 8 dígitos sai truncada, sem erro. Locale fixo por moeda (pt-BR/en-US/de-DE) e
 * pt-BR para inteiro/percentual — não segue o idioma da UI.
 *
 * Usado por: `apps/admin` — KPIs, tabelas e charts de `dashboard-finance`, `dashboard-sales`,
 * `dashboard-churn`, `dashboard-customers`, `dashboard-usage*`, `dashboard-product`,
 * `dashboard-health`, e as rotas `Finance*Page`/`BillingSubscriptionsPage` via
 * `formatCurrencyCents`. O `formatPct` de `apps/marketing/src/lib/lossEstimate.ts` é homônimo,
 * não este.
 *
 * Dicas: `CURRENCY_FORMATTERS` é `Record<Currency, …>` — moeda nova em `@etiquetachef/types`
 * reprova o typecheck aqui até ganhar formatter, de propósito.
 */
const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CURRENCY_FORMATTERS: Record<Currency, Intl.NumberFormat> = {
  BRL: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }),
  USD: USD_FORMATTER,
  EUR: new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }),
};

/** Centavos de USD → `$1,234.56` (en-US). */
export function formatUsdCents(cents: number): string {
  return USD_FORMATTER.format(cents / 100);
}

/** Centavos na moeda dada → string no locale fixo da moeda (BRL pt-BR, EUR de-DE). */
export function formatCurrencyCents(cents: number, currency: Currency): string {
  return CURRENCY_FORMATTERS[currency].format(cents / 100);
}

/** Inteiro com separador de milhar pt-BR. */
export function formatInt(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value);
}

/** Fração (0.153) → `15,3%` em pt-BR; não recebe percentual já multiplicado. */
export function formatPct(value: number, fractionDigits = 1): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Variação fracionária → rótulo `+15,3%` e direção para o ícone do `KpiCard`. */
export function formatDelta(value: number | null): {
  label: string;
  direction: 'UP' | 'DOWN' | 'FLAT' | 'NA';
} {
  if (value === null || !Number.isFinite(value)) {
    return { label: '—', direction: 'NA' };
  }
  if (value === 0) return { label: '—', direction: 'FLAT' };
  const sign = value > 0 ? '+' : '';
  return {
    label: `${sign}${formatPct(value)}`,
    direction: value > 0 ? 'UP' : 'DOWN',
  };
}

/** `yyyymmdd` → `dd/mm/aaaa`, sem validar (entrada curta sai truncada). */
export function formatYyyymmddDisplay(yyyymmdd: string): string {
  const yyyy = yyyymmdd.slice(0, 4);
  const mm = yyyymmdd.slice(4, 6);
  const dd = yyyymmdd.slice(6, 8);
  return `${dd}/${mm}/${yyyy}`;
}
